/**
 * Space Detail Can-Deactivate Guard
 *
 * Prompts the user before navigating away from the Space Detail page when
 * there are unsaved changes. The guarded component must expose a `dirty()`
 * signal (or any zero-arg function returning a boolean) — the guard reads
 * that and surfaces a native `confirm()` dialog when truthy.
 *
 * RESPONSIBILITY: Hard navigation guard for the `/admin/spaces/:id` route.
 * Pairs with the existing `beforeunload` handler in the shell, which guards
 * full page reloads / tab close.
 *
 * AI CONTEXT: We deliberately use `confirm()` (not a custom modal) here so
 * that the dialog appears synchronously *before* the route activates. This
 * keeps the guard side-effect free and avoids navigation race conditions.
 */
import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

export interface SpaceDetailDirtyHost {
  /** Returns true when the form has unsaved edits. */
  dirty: () => boolean;
  /**
   * Optional: unsaved work the page's save bar does not carry — a draft with
   * its own Save, such as a visitor type's email being reworded. Leaving
   * loses it just the same, so it asks just the same.
   */
  unsavedDraft?: () => boolean;
}

export const spaceDetailCanDeactivateGuard: CanDeactivateFn<SpaceDetailDirtyHost> = (
  component,
) => {
  if (!component || typeof component.dirty !== 'function') return true;
  const draft = typeof component.unsavedDraft === 'function' && component.unsavedDraft();
  if (!component.dirty() && !draft) return true;
  const transloco = inject(TranslocoService);
  return window.confirm(transloco.translate('shared.confirms.discard_changes'));
};
